import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  ShieldCheck, Search, RefreshCw, Download, Link2, Unlink, QrCode, Mail, MessageSquare,
  FileText, Copy, Loader2, HeartPulse, CheckCircle2, PartyPopper,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(' ');
  return fb || 'Something went wrong';
};

const ALPHA = ['All', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

const blobDownload = async (url, filename) => {
  try {
    const res = await axios.get(url, { ...cfg, responseType: 'blob' });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = href; a.download = filename; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(href);
  } catch (e) { toast.error(errMsg(e, 'Download failed')); }
};

// ─────────────────────────  WAIVERS TAB  ─────────────────────────
const WaiversTab = () => {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [letter, setLetter] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [matches, setMatches] = useState([]);
  const [linkSearch, setLinkSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/waivers`, {
        ...cfg, params: { q, letter, status, page, page_size: pageSize },
      });
      setRows(data.waivers || []); setCounts(data.counts || {}); setTotal(data.total || 0);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [q, letter, status, page]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (form_id) => {
    setDetailLoading(true); setDetail({ form_id });
    try {
      const { data } = await axios.get(`${API}/api/admin/waivers/${form_id}`, cfg);
      setDetail(data);
    } catch (e) { toast.error(errMsg(e)); setDetail(null); } finally { setDetailLoading(false); }
  };

  const openLink = async (w) => {
    setLinkTarget(w); setLinkSearch(''); setMatches([]);
  };
  const searchMatches = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/waivers/booking-matches`, { ...cfg, params: { name: linkSearch } });
      setMatches(data.matches || []);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doLink = async (booking_id) => {
    try {
      await axios.post(`${API}/api/admin/waivers/${linkTarget.form_id}/link`, { booking_id }, cfg);
      toast.success('Waiver linked to booking'); setLinkTarget(null); await load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doUnlink = async (w) => {
    try {
      await axios.post(`${API}/api/admin/waivers/${w.form_id}/unlink`, {}, cfg);
      toast.success('Waiver unlinked'); await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const statusBadge = (w) => {
    if (!w.is_valid) return <Badge className="bg-gray-200 text-gray-700" data-testid={`wv-status-${w.form_id}`}>Expired</Badge>;
    return <Badge className="bg-[#06D6A0]/15 text-[#06B88A]" data-testid={`wv-status-${w.form_id}`}>Valid</Badge>;
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search child, parent, email or phone…" className="pl-9 rounded-full border-[#E8E5DF]" data-testid="wv-search" />
        </div>
        <div className="flex gap-1.5">
          {[['all', 'All'], ['valid', 'Valid'], ['expired', 'Expired'], ['linked', 'Linked'], ['unlinked', 'Unlinked']].map(([v, l]) => (
            <button key={v} onClick={() => { setStatus(v); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${status === v ? 'bg-[#1E1E24] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A]'}`}
              data-testid={`wv-filter-${v}`}>
              {l}{counts[v] != null ? ` ${counts[v]}` : ''}
            </button>
          ))}
        </div>
        <Button variant="outline" onClick={load} className="rounded-full" data-testid="wv-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* A-Z */}
      <div className="flex flex-wrap gap-1 mb-4" data-testid="wv-alpha">
        {ALPHA.map((L) => {
          const val = L === 'All' ? '' : L;
          const active = letter === val;
          return (
            <button key={L} onClick={() => { setLetter(val); setPage(1); }}
              className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${active ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'}`}
              data-testid={`wv-alpha-${L}`}>{L === 'All' ? '·' : L}</button>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Guest child</TableHead><TableHead>Parent / Guardian</TableHead><TableHead>Contact</TableHead>
            <TableHead>Signed</TableHead><TableHead>Valid until</TableHead><TableHead>Status</TableHead>
            <TableHead>Party (birthday child)</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-[#6B6B7A]">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-[#6B6B7A]">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-40" /> No waivers found.
              </TableCell></TableRow>
            ) : rows.map((w) => (
              <TableRow key={w.form_id} data-testid={`wv-row-${w.form_id}`}>
                <TableCell>
                  <button onClick={() => openDetail(w.form_id)} className="font-medium text-[#1E1E24] hover:text-[#FF3B6B] text-left flex items-center gap-1" data-testid={`wv-open-${w.form_id}`}>
                    {w.child_name}{w.has_medical ? <HeartPulse className="w-3 h-3 text-[#FF3B6B]" title="Has medical info" /> : null}
                  </button>
                  {w.child_age != null && <div className="text-xs text-[#9CA3AF]">Age {w.child_age}</div>}
                </TableCell>
                <TableCell className="text-sm">{w.guardian_name}</TableCell>
                <TableCell className="text-xs text-[#6B6B7A]">{w.guardian_email || '—'}<br />{w.guardian_phone || ''}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{(w.signed_at || '').slice(0, 10)}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{(w.valid_until || '').slice(0, 10)}</TableCell>
                <TableCell>{statusBadge(w)}</TableCell>
                <TableCell>
                  {w.booking_id ? (
                    <div className="text-xs" data-testid={`wv-party-${w.form_id}`}>
                      <div className="font-medium text-[#1E1E24] flex items-center gap-1">
                        <PartyPopper className="w-3 h-3 text-[#FF3B6B]" />
                        {w.booking_child_name || 'Linked booking'}
                      </div>
                      {w.booking_date && <div className="text-[#9CA3AF]">{w.booking_date}</div>}
                    </div>
                  ) : <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Unlinked</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => blobDownload(`${API}/api/admin/waivers/${w.form_id}/pdf`, `waiver_${w.child_name}.pdf`)} title="Download PDF" data-testid={`wv-pdf-${w.form_id}`}><Download className="w-4 h-4" /></Button>
                    {w.booking_id
                      ? <Button size="sm" variant="ghost" onClick={() => doUnlink(w)} title="Unlink booking" data-testid={`wv-unlink-${w.form_id}`}><Unlink className="w-4 h-4" /></Button>
                      : <Button size="sm" variant="ghost" onClick={() => openLink(w)} title="Link to booking" data-testid={`wv-link-${w.form_id}`}><Link2 className="w-4 h-4" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-full" data-testid="wv-prev">Prev</Button>
          <span className="text-sm text-[#6B6B7A]">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-full" data-testid="wv-next">Next</Button>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-lg bg-white" data-testid="wv-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.child_name || 'Waiver'}</DialogTitle>
            <DialogDescription>Signed waiver details</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-8 text-center text-[#6B6B7A]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : detail && (
            <div className="space-y-2 text-sm">
              <Row label="Parent / Guardian" value={detail.guardian_name} />
              <Row label="Email" value={detail.guardian_email || '—'} />
              <Row label="Phone" value={detail.guardian_phone || '—'} />
              <Row label="Signature" value={detail.signature_name} />
              <Row label="Signed" value={(detail.signed_at || '').slice(0, 10)} />
              <Row label="Valid until" value={(detail.valid_until || '').slice(0, 10)} />
              <Row label="Photo/video consent" value={detail.photo_video_consent ? 'Granted' : 'Declined'} />
              {detail.booking && <Row label="Party (birthday child)" value={`${detail.booking.child_name} · ${detail.booking.date || ''} ${detail.booking.time_slot || ''}`} />}
              {detail.medical_data && (
                <div className="mt-3 pt-3 border-t border-[#F0EEE9]">
                  <p className="text-xs font-semibold text-[#FF3B6B] flex items-center gap-1 mb-1"><HeartPulse className="w-3 h-3" /> Medical (encrypted at rest)</p>
                  <Row label="Allergies" value={[...(detail.medical_data.allergies || []), detail.medical_data.allergy_other].filter(Boolean).join(', ') || 'None'} />
                  <Row label="Conditions" value={[...(detail.medical_data.medical_conditions || []), detail.medical_data.medical_other].filter(Boolean).join(', ') || 'None'} />
                  {detail.medical_data.emergency_contact_name && (
                    <Row label="Emergency contact" value={`${detail.medical_data.emergency_contact_name} (${detail.medical_data.emergency_contact_relationship || ''}) ${detail.medical_data.emergency_contact_phone || ''}`} />
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {detail?.form_id && (
              <Button variant="outline" onClick={() => blobDownload(`${API}/api/admin/waivers/${detail.form_id}/pdf`, `waiver_${detail.child_name}.pdf`)} className="rounded-full" data-testid="wv-detail-pdf"><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link dialog */}
      <Dialog open={!!linkTarget} onOpenChange={(o) => { if (!o) setLinkTarget(null); }}>
        <DialogContent className="max-w-lg bg-white" data-testid="wv-link-dialog">
          <DialogHeader>
            <DialogTitle>Link waiver to a party</DialogTitle>
            <DialogDescription>
              Waiver signed for guest <strong>{linkTarget?.child_name}</strong>. Search by the
              <strong> birthday child's name</strong> to find the party this guest is attending.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} placeholder="Birthday child's name…" className="rounded-full" data-testid="wv-link-search" />
            <Button onClick={searchMatches} className="rounded-full" data-testid="wv-link-search-btn"><Search className="w-4 h-4" /></Button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-2 mt-2">
            {matches.length === 0 ? <p className="text-sm text-[#9CA3AF] text-center py-6">Search for the birthday child to see matching parties.</p> : matches.map((m) => (
              <button key={m.booking_id} onClick={() => doLink(m.booking_id)}
                className="w-full text-left p-3 rounded-xl border border-[#E8E5DF] hover:border-[#FF3B6B] hover:bg-[#FFFDF8] transition-colors" data-testid={`wv-match-${m.booking_id}`}>
                <div className="font-medium text-[#1E1E24] text-sm flex items-center gap-1">
                  <PartyPopper className="w-3 h-3 text-[#FF3B6B]" />{m.child_name}'s party
                  <span className="text-[#9CA3AF] font-normal">· {m.customer_name}</span>
                </div>
                <div className="text-xs text-[#6B6B7A]">{m.date} {m.time_slot} · {m.lifecycle_status || m.status}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-[#9CA3AF]">{label}</span>
    <span className="text-[#1E1E24] text-right font-medium">{value}</span>
  </div>
);

// ─────────────────────────  SHARE TAB  ─────────────────────────
const ShareTab = () => {
  const [qrUrl, setQrUrl] = useState('');
  const [channel, setChannel] = useState('email');
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const publicLink = `${API}/waiver`;

  useEffect(() => {
    let revoked = false; let objUrl = '';
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/waivers/qr`, { ...cfg, responseType: 'blob' });
        if (revoked) return;
        objUrl = URL.createObjectURL(res.data); setQrUrl(objUrl);
      } catch (e) { /* silent */ }
    })();
    return () => { revoked = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, []);

  const copyLink = async () => { try { await navigator.clipboard.writeText(publicLink); toast.success('Link copied'); } catch { toast.error('Copy failed'); } };

  const send = async () => {
    if (!to.trim()) return toast.error('Enter a recipient');
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/waivers/share`, { channel, to, message }, cfg);
      toast.success(`Waiver link sent via ${channel}${data.provider === 'mock' ? ' (mock)' : ''}`);
      setTo(''); setMessage('');
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6" data-testid="wv-share-qr">
        <h3 className="font-semibold text-[#1E1E24] flex items-center gap-2 mb-3"><QrCode className="w-5 h-5 text-[#FF3B6B]" /> QR Code</h3>
        <p className="text-sm text-[#6B6B7A] mb-4">Print this or show it at the studio — parents scan to sign on their phone.</p>
        <div className="flex justify-center bg-[#FBFAF7] rounded-2xl p-6 border border-[#E8E5DF]">
          {qrUrl ? <img src={qrUrl} alt="Waiver QR code" className="w-48 h-48" data-testid="wv-qr-img" /> : <div className="w-48 h-48 flex items-center justify-center text-[#9CA3AF]"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Input readOnly value={publicLink} className="rounded-full text-xs border-[#E8E5DF]" data-testid="wv-public-link" />
          <Button variant="outline" onClick={copyLink} className="rounded-full flex-shrink-0" data-testid="wv-copy-link"><Copy className="w-4 h-4" /></Button>
        </div>
        <a href={`${API}/api/admin/waivers/qr`} download="waiver-qr.png" className="text-xs text-[#FF3B6B] hover:underline mt-3 inline-block" data-testid="wv-qr-download">Download QR image</a>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6" data-testid="wv-share-send">
        <h3 className="font-semibold text-[#1E1E24] flex items-center gap-2 mb-3"><MessageSquare className="w-5 h-5 text-[#FF3B6B]" /> Send the link</h3>
        <div className="flex gap-2 mb-4">
          {[['email', 'Email', Mail], ['sms', 'SMS', MessageSquare]].map(([v, l, Icon]) => (
            <button key={v} onClick={() => setChannel(v)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium border transition-colors ${channel === v ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]' : 'bg-white text-[#6B6B7A] border-[#E8E5DF]'}`}
              data-testid={`wv-channel-${v}`}><Icon className="w-4 h-4" /> {l}</button>
          ))}
        </div>
        <Label className="text-xs text-[#6B6B7A]">{channel === 'email' ? 'Recipient email' : 'Recipient phone'}</Label>
        <Input value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 mb-3 rounded-xl border-[#E8E5DF]" placeholder={channel === 'email' ? 'parent@email.com' : '905-555-1234'} data-testid="wv-share-to" />
        <Label className="text-xs text-[#6B6B7A]">Message (optional)</Label>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1 mb-4 rounded-xl border-[#E8E5DF]" rows={3} placeholder="Add a personal note…" data-testid="wv-share-message" />
        <Button onClick={send} disabled={sending} className="w-full rounded-full bg-[#1E1E24] hover:bg-black text-white" data-testid="wv-share-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send via ${channel}`}
        </Button>
      </div>
    </div>
  );
};

// ─────────────────────────  WAIVER TEXT TAB  ─────────────────────────
const WaiverTextTab = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [version, setVersion] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/waiver-text`, cfg);
      setTitle(data.title || ''); setBody(data.body || ''); setVersion(data.version || 1);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!title.trim() || !body.trim()) return toast.error('Title and body are required');
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/admin/settings/waiver-text`, { title, body }, cfg);
      setVersion(data.version); toast.success(`Waiver text saved (v${data.version})`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6 max-w-3xl" data-testid="wv-text-editor">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-[#FF3B6B]" />
        <h3 className="font-semibold text-[#1E1E24]">Editable waiver text</h3>
        <Badge variant="outline" className="ml-2">v{version}</Badge>
      </div>
      <p className="text-sm text-[#6B6B7A] mb-4">This is the legal text parents read and accept on the public form. Saving bumps the version stamped onto new signatures.</p>
      {loading ? <div className="py-8 text-center text-[#6B6B7A]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
        <>
          <Label className="text-xs text-[#6B6B7A]">Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 mb-4 rounded-xl border-[#E8E5DF]" data-testid="wv-text-title" />
          <Label className="text-xs text-[#6B6B7A]">Body</Label>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} className="mt-1 mb-4 rounded-xl border-[#E8E5DF] font-mono text-xs" rows={16} data-testid="wv-text-body" />
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="rounded-full bg-[#FF3B6B] hover:bg-[#E6325F] text-white" data-testid="wv-text-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save waiver text'}
            </Button>
            <Button variant="outline" onClick={load} className="rounded-full" data-testid="wv-text-reset">Reset</Button>
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────  PAGE  ─────────────────────────
export const CrmWaivers = () => {
  return (
    <div className="max-w-6xl mx-auto" data-testid="crm-waivers">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-[#FF3B6B]/10 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-[#FF3B6B]" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Liability Waivers</h1>
          <p className="text-sm text-[#6B6B7A]">Signed waivers, sharing tools and the editable waiver text — all in one place.</p>
        </div>
      </div>

      <Tabs defaultValue="waivers">
        <TabsList className="mb-5">
          <TabsTrigger value="waivers" data-testid="wv-tab-waivers"><ShieldCheck className="w-4 h-4 mr-1" /> Waivers</TabsTrigger>
          <TabsTrigger value="share" data-testid="wv-tab-share"><QrCode className="w-4 h-4 mr-1" /> Share</TabsTrigger>
          <TabsTrigger value="text" data-testid="wv-tab-text"><FileText className="w-4 h-4 mr-1" /> Waiver Text</TabsTrigger>
        </TabsList>
        <TabsContent value="waivers"><WaiversTab /></TabsContent>
        <TabsContent value="share"><ShareTab /></TabsContent>
        <TabsContent value="text"><WaiverTextTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default CrmWaivers;
